import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
  Stack,
} from '@mui/material';
import { Add, Edit, Delete, Refresh } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import axios from 'axios';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Navbar from './Navbar';

const ACCOUNT_TYPES = ['ASSET', 'LIABILITY', 'EQUITY', 'REVENUE', 'EXPENSE'];
const BALANCE_SIDES = ['DEBIT', 'CREDIT'];

const EMPTY_FORM = {
  accountCode: '',
  accountName: '',
  accountType: 'ASSET',
  parentAccountID: '',
  openingBalance: '0',
  balanceSide: 'DEBIT',
  isActive: 1,
  remarks: '',
};

const statusChipColor = (isActive) => (Number(isActive) === 1 ? 'success' : 'default');

export default function AccountsManagement() {
  const user = useSelector((state) => state.auth.user);
  const cmpID = Number(user?.cmpID || 1);

  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentAccount, setCurrentAccount] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busyAction, setBusyAction] = useState('');

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const importInputRef = React.useRef(null);

  const fetchAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${global.SET_API_URL}accounts/`, {
        params: {
          cmpID,
          search: search.trim() || undefined,
          accountType: typeFilter || undefined,
          status: statusFilter === 'all' ? undefined : statusFilter,
        },
      });

      setAccounts(response?.data?.data || []);
      setError('');
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load accounts.');
    } finally {
      setLoading(false);
    }
  }, [cmpID, search, statusFilter, typeFilter]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const parentOptions = useMemo(() => {
    if (!currentAccount?.accountID) {
      return accounts;
    }

    return accounts.filter((a) => Number(a.accountID) !== Number(currentAccount.accountID));
  }, [accounts, currentAccount]);

  const clearMessagesSoon = () => {
    window.setTimeout(() => {
      setSuccess('');
      setError('');
    }, 3000);
  };

  const openForCreate = () => {
    setCurrentAccount(null);
    setFormData(EMPTY_FORM);
    setOpenDialog(true);
  };

  const openForEdit = (account) => {
    setCurrentAccount(account);
    setFormData({
      accountCode: account.accountCode || '',
      accountName: account.accountName || '',
      accountType: account.accountType || 'ASSET',
      parentAccountID: account.parentAccountID || '',
      openingBalance: String(account.openingBalance ?? 0),
      balanceSide: account.balanceSide || 'DEBIT',
      isActive: Number(account.isActive) === 0 ? 0 : 1,
      remarks: account.remarks || '',
    });
    setOpenDialog(true);
  };

  const closeDialog = () => {
    setOpenDialog(false);
    setCurrentAccount(null);
    setFormData(EMPTY_FORM);
  };

  const handleChange = (key) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    try {
      setError('');
      setSuccess('');

      const payload = {
        cmpID,
        accountCode: String(formData.accountCode || '').trim(),
        accountName: String(formData.accountName || '').trim(),
        accountType: formData.accountType,
        parentAccountID: formData.parentAccountID || null,
        openingBalance: Number(formData.openingBalance || 0),
        balanceSide: formData.balanceSide,
        isActive: Number(formData.isActive) === 0 ? 0 : 1,
        remarks: String(formData.remarks || '').trim(),
        updatedBy: user?.userName || user?.dispName || 'web-admin',
      };

      if (!payload.accountCode || !payload.accountName || !payload.accountType) {
        setError('Account code, account name and account type are required.');
        return;
      }

      if (currentAccount?.accountID) {
        await axios.put(`${global.SET_API_URL}accounts/${currentAccount.accountID}`, payload);
        setSuccess('Account updated successfully.');
      } else {
        await axios.post(`${global.SET_API_URL}accounts/`, payload);
        setSuccess('Account created successfully.');
      }

      closeDialog();
      fetchAccounts();
      clearMessagesSoon();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save account.');
    }
  };

  const handleDelete = async (accountID) => {
    if (!window.confirm('Are you sure you want to delete this account?')) {
      return;
    }

    try {
      setError('');
      setSuccess('');
      await axios.delete(`${global.SET_API_URL}accounts/${accountID}`);
      setSuccess('Account deleted successfully.');
      fetchAccounts();
      clearMessagesSoon();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to delete account.');
    }
  };

  const handleBuildChart = async () => {
    try {
      setBusyAction('build');
      setError('');
      setSuccess('');

      const response = await axios.post(`${global.SET_API_URL}accounts/build-chart`, {
        cmpID,
        updatedBy: user?.userName || user?.dispName || 'web-admin',
      });

      const message = response?.data?.message || 'Chart of accounts built successfully.';
      setSuccess(message);
      fetchAccounts();
      clearMessagesSoon();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to build chart of accounts.');
    } finally {
      setBusyAction('');
    }
  };

  const handleExportExcel = () => {
    if (!accounts.length) {
      setError('No accounts available to export.');
      clearMessagesSoon();
      return;
    }

    const rows = accounts.map((account) => ({
      accountCode: account.accountCode,
      accountName: account.accountName,
      accountType: account.accountType,
      parentAccountCode: account.parentAccountID || '',
      parentAccountName: account.parentAccountName || '',
      openingBalance: Number(account.openingBalance || 0),
      balanceSide: account.balanceSide,
      isActive: Number(account.isActive) === 1 ? 1 : 0,
      remarks: account.remarks || '',
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Accounts');
    XLSX.writeFile(workbook, 'accounts_chart_export.xlsx');
  };

  const handleExportPdf = () => {
    if (!accounts.length) {
      setError('No accounts available to export.');
      clearMessagesSoon();
      return;
    }

    const doc = new jsPDF();
    doc.setFontSize(12);
    doc.text('Chart of Accounts', 14, 14);

    const body = accounts.map((account) => ([
      account.accountCode,
      account.accountName,
      account.accountType,
      account.parentAccountName || '-',
      Number(account.openingBalance || 0).toFixed(2),
      account.balanceSide,
      Number(account.isActive) === 1 ? 'Active' : 'Inactive',
    ]));

    autoTable(doc, {
      head: [['Code', 'Name', 'Type', 'Parent', 'Opening', 'Side', 'Status']],
      body,
      startY: 20,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [25, 118, 210] },
    });

    doc.save('accounts_chart_export.pdf');
  };

  const handleDownloadTemplate = () => {
    const templateRows = [
      {
        accountCode: '1000',
        accountName: 'Assets',
        accountType: 'ASSET',
        parentAccountCode: '',
        openingBalance: 0,
        balanceSide: 'DEBIT',
        isActive: 1,
        remarks: 'Top level account',
      },
      {
        accountCode: '1100',
        accountName: 'Cash in Hand',
        accountType: 'ASSET',
        parentAccountCode: '1000',
        openingBalance: 0,
        balanceSide: 'DEBIT',
        isActive: 1,
        remarks: '',
      },
    ];

    const worksheet = XLSX.utils.json_to_sheet(templateRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'AccountsTemplate');
    XLSX.writeFile(workbook, 'accounts_import_template.xlsx');
  };

  const normalizeImportRow = (row) => {
    const source = row || {};
    const getValue = (...keys) => {
      for (const key of keys) {
        if (Object.prototype.hasOwnProperty.call(source, key) && source[key] !== undefined && source[key] !== null) {
          return source[key];
        }
      }
      return '';
    };

    return {
      accountCode: String(getValue('accountCode', 'Account Code', 'Code')).trim(),
      accountName: String(getValue('accountName', 'Account Name', 'Name')).trim(),
      accountType: String(getValue('accountType', 'Account Type', 'Type')).trim().toUpperCase(),
      parentAccountCode: String(getValue('parentAccountCode', 'Parent Account Code', 'Parent Code')).trim(),
      openingBalance: Number(getValue('openingBalance', 'Opening Balance', 'Opening') || 0),
      balanceSide: String(getValue('balanceSide', 'Balance Side', 'Side') || 'DEBIT').trim().toUpperCase(),
      isActive: Number(getValue('isActive', 'Is Active', 'Status') || 1) === 0 ? 0 : 1,
      remarks: String(getValue('remarks', 'Remarks') || '').trim(),
    };
  };

  const handleImportFile = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) {
      return;
    }

    try {
      setBusyAction('import');
      setError('');
      setSuccess('');

      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      const rawRows = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

      const accountsForImport = rawRows
        .map(normalizeImportRow)
        .filter((row) => row.accountCode && row.accountName && row.accountType);

      if (!accountsForImport.length) {
        setError('Import file has no valid rows. Please use the template.');
        return;
      }

      const response = await axios.post(`${global.SET_API_URL}accounts/import`, {
        cmpID,
        updatedBy: user?.userName || user?.dispName || 'web-admin',
        accounts: accountsForImport,
      });

      const result = response?.data?.data || {};
      const summary = `Import completed. Created: ${result.created || 0}, Updated: ${result.updated || 0}, Failed: ${result.failed || 0}.`;
      setSuccess(summary);
      fetchAccounts();
      clearMessagesSoon();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to import accounts.');
    } finally {
      if (importInputRef.current) {
        importInputRef.current.value = '';
      }
      setBusyAction('');
    }
  };

  return (
    <Box>
      <Navbar />
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h5" fontWeight={700}>Accounts Module</Typography>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<Refresh />} onClick={fetchAccounts}>Refresh</Button>
            <Button variant="outlined" onClick={handleBuildChart} disabled={busyAction === 'build'}>
              {busyAction === 'build' ? 'Building...' : 'Build Chart of Accounts'}
            </Button>
            <Button variant="outlined" onClick={handleDownloadTemplate}>Download Import Template</Button>
            <Button variant="outlined" onClick={() => importInputRef.current?.click()} disabled={busyAction === 'import'}>
              {busyAction === 'import' ? 'Importing...' : 'Import Excel'}
            </Button>
            <Button variant="outlined" onClick={handleExportExcel}>Export Excel</Button>
            <Button variant="outlined" onClick={handleExportPdf}>Export PDF</Button>
            <Button variant="contained" startIcon={<Add />} onClick={openForCreate}>Add Account</Button>
          </Stack>
        </Box>

        <input
          ref={importInputRef}
          type="file"
          accept=".xlsx,.xls"
          style={{ display: 'none' }}
          onChange={handleImportFile}
        />

        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
        {success ? <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert> : null}

        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Code or name"
              fullWidth
            />

            <FormControl sx={{ minWidth: 180 }}>
              <InputLabel>Account Type</InputLabel>
              <Select value={typeFilter} label="Account Type" onChange={(e) => setTypeFilter(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                {ACCOUNT_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 160 }}>
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>

            <Button variant="outlined" onClick={fetchAccounts}>Apply</Button>
          </Stack>
        </Paper>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Account Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Parent</TableCell>
                <TableCell align="right">Opening Balance</TableCell>
                <TableCell>Balance Side</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && accounts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">No accounts found.</TableCell>
                </TableRow>
              ) : null}

              {accounts.map((account) => (
                <TableRow key={account.accountID}>
                  <TableCell>{account.accountCode}</TableCell>
                  <TableCell>{account.accountName}</TableCell>
                  <TableCell>{account.accountType}</TableCell>
                  <TableCell>{account.parentAccountName || '-'}</TableCell>
                  <TableCell align="right">{Number(account.openingBalance || 0).toFixed(2)}</TableCell>
                  <TableCell>{account.balanceSide}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={Number(account.isActive) === 1 ? 'Active' : 'Inactive'}
                      color={statusChipColor(account.isActive)}
                    />
                  </TableCell>
                  <TableCell>
                    <Button size="small" startIcon={<Edit />} onClick={() => openForEdit(account)}>Edit</Button>
                    <Button size="small" color="error" startIcon={<Delete />} onClick={() => handleDelete(account.accountID)}>Delete</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog open={openDialog} onClose={closeDialog} fullWidth maxWidth="sm">
          <DialogTitle>{currentAccount ? 'Edit Account' : 'Add Account'}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Account Code"
                value={formData.accountCode}
                onChange={handleChange('accountCode')}
                required
              />

              <TextField
                label="Account Name"
                value={formData.accountName}
                onChange={handleChange('accountName')}
                required
              />

              <FormControl fullWidth>
                <InputLabel>Account Type</InputLabel>
                <Select
                  value={formData.accountType}
                  label="Account Type"
                  onChange={handleChange('accountType')}
                >
                  {ACCOUNT_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Parent Account</InputLabel>
                <Select
                  value={formData.parentAccountID}
                  label="Parent Account"
                  onChange={handleChange('parentAccountID')}
                >
                  <MenuItem value="">None</MenuItem>
                  {parentOptions.map((item) => (
                    <MenuItem key={item.accountID} value={item.accountID}>
                      {item.accountCode} - {item.accountName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Opening Balance"
                type="number"
                inputProps={{ min: 0, step: '0.01' }}
                value={formData.openingBalance}
                onChange={handleChange('openingBalance')}
              />

              <FormControl fullWidth>
                <InputLabel>Balance Side</InputLabel>
                <Select
                  value={formData.balanceSide}
                  label="Balance Side"
                  onChange={handleChange('balanceSide')}
                >
                  {BALANCE_SIDES.map((side) => (
                    <MenuItem key={side} value={side}>{side}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.isActive}
                  label="Status"
                  onChange={handleChange('isActive')}
                >
                  <MenuItem value={1}>Active</MenuItem>
                  <MenuItem value={0}>Inactive</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Remarks"
                value={formData.remarks}
                onChange={handleChange('remarks')}
                multiline
                minRows={2}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog}>Cancel</Button>
            <Button variant="contained" onClick={handleSave}>Save</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}
